import { FormEvent, useCallback, useEffect, useState } from 'react'
import { dripAPI } from '../../services/api'
import type { DripPiece, SubscriptionTier } from '../../types'
import { apiErrorMessage } from '../../utils/apiError'

/**
 * Monetization → Tiers: pieces subscribers unlock some days after they
 * subscribe (all subscribers, or one tier's). Checked when written, like
 * every post; emailed with the disclaimer when they unlock (hourly).
 */
const DripSchedule = ({ tiers }: { tiers: SubscriptionTier[] }) => {
  const [pieces, setPieces] = useState<DripPiece[] | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [day, setDay] = useState('7')
  const [tierId, setTierId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    // Errors (even a synchronous one) stay in this panel, never the page.
    Promise.resolve()
      .then(() => dripAPI.schedule())
      .then(setPieces)
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load your drip schedule.")))
  }, [])
  useEffect(load, [load])

  const add = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await dripAPI.create({ title: title.trim(), body, unlock_day: Number(day), tier_id: tierId ? Number(tierId) : null })
      setTitle('')
      setBody('')
      load()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't add it."))
    } finally {
      setSaving(false)
    }
  }

  const remove = async (id: number) => {
    try {
      await dripAPI.remove(id)
      load()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't remove it."))
    }
  }

  const tierName = (id: number | null) => (id ? tiers.find((t) => t.id === id)?.name ?? 'One tier' : 'All subscribers')

  return (
    <section className="rounded-lg bg-white p-4 shadow" aria-label="Drip content">
      <h3 className="text-lg font-semibold text-gray-900">Drip content</h3>
      <p className="mt-1 text-sm text-gray-600">
        Pieces your subscribers unlock a set number of days after they subscribe. Each is emailed to them when it
        unlocks.
      </p>
      {pieces && pieces.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 text-sm" aria-label="Drip schedule">
          {pieces.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-x-3 py-2">
              <span className="w-16 shrink-0 text-gray-500">Day {p.unlock_day}</span>
              <span className="font-medium text-gray-900">{p.title}</span>
              <span className="text-xs text-gray-500">{tierName(p.tier_id)}</span>
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="ml-auto text-xs text-gray-500 hover:underline"
                aria-label={`Remove ${p.title}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="mt-4 space-y-2">
        <div className="flex flex-wrap gap-2">
          <label className="text-sm text-gray-700">
            Unlocks on day
            <input
              type="number"
              min={1}
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="ml-2 w-20 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-sm text-gray-700">
            For
            <select
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              className="ml-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
            >
              <option value="">All subscribers</option>
              {tiers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <input
          aria-label="Title"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
        />
        <textarea
          aria-label="Text"
          placeholder="What they get"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-gray-300 p-2 text-sm"
        />
        <button
          type="submit"
          disabled={saving || !title.trim() || !body.trim() || Number(day) < 1}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add to schedule'}
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}
    </section>
  )
}

export default DripSchedule
